import { useAuth, useLocalStorage } from "@elit-hair/elit-tools";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";

import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { chatbotService } from "@/services/chatbotService";
import { ChatMessageRole, ChatMessage as ChatMessageType, StreamEventData } from "@/types/chatbot.types";

/**
 * Helper function to create stream event handler
 */
function createStreamEventHandler(
    streamingMessageRef: React.MutableRefObject<ChatMessageType | null>,
    streamingMessageId: string,
    setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>
) {
    return (streamEvent: StreamEventData) => {
        if (!streamingMessageRef.current) {
            return;
        }

        switch (streamEvent.type) {
            case "token":
                // Append token to current content
                streamingMessageRef.current.content += streamEvent.data;
                streamingMessageRef.current.createdAt = streamEvent.timestamp
                    ? DateTime.fromMillis(streamEvent.timestamp).toISO() || DateTime.now().toISO() // fallback ensures ISO string
                    : DateTime.now().toISO();

                // Update messages with new content
                setMessages((currentMessages) => {
                    // If streamingMessageRef is null, ignore this token (late token after completion)
                    if (!streamingMessageRef.current) {
                        return currentMessages;
                    }

                    const updatedMessages = [...currentMessages];
                    const lastMessageIndex = updatedMessages.length - 1;

                    // Check if this is the first token and we need to add the streaming message
                    if (
                        lastMessageIndex < 0 ||
                        updatedMessages[lastMessageIndex].role !== ChatMessageRole.ASSISTANT ||
                        updatedMessages[lastMessageIndex].id !== streamingMessageId
                    ) {
                        // Add the streaming message for the first time with correct ID
                        const messageToAdd = {
                            ...streamingMessageRef.current!,
                            id: streamingMessageId,
                        };
                        updatedMessages.push(messageToAdd);
                    } else {
                        // Update existing streaming message
                        updatedMessages[lastMessageIndex] = { ...streamingMessageRef.current! };
                    }

                    return updatedMessages;
                });
                break;

            case "chunk":
                // Handle source chunks (sources info)
                try {
                    const chunkData = JSON.parse(streamEvent.data);
                    if (chunkData.sources) {
                        console.log("Received sources:", chunkData.sources);
                    }
                } catch (error) {
                    console.warn("Failed to parse chunk data:", error);
                }
                break;

            case "error":
                console.error("Stream error:", streamEvent.data);
                break;
        }
    };
}

export type UseChatStreamReturn = {
    messages: ChatMessageType[];
    input: string;
    handleInputChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => Promise<void>;
    clearHistory: () => void;
    isLoading: boolean;
    isStreaming: boolean;
    stopStreaming: () => void;
};

/**
 * STREAMING CHAT HOOK - Server-sent events approach
 * Uses streaming API endpoint for real-time response chunks
 *
 * This hook provides the same types as use-chat but implements streaming internally
 */

export function generateChatId() {
    const [chatId] = useLocalStorage<string>(
        LOCAL_STORAGE_KEYS.AI_CHAT_SESSION_ID.key,
        `chat-${DateTime.now().toMillis()}`
    );
    return chatId;
}

export function useChatStream() {
    const { auth, userData } = useAuth();
    const chatId = generateChatId();

    const [allUserHistories, setAllUserHistories] = useLocalStorage<Record<string, ChatMessageType[]>>(
        LOCAL_STORAGE_KEYS.AI_CHAT_HISTORY.key,
        {}
    );

    const getCurrentUserId = () => {
        const userId = userData?.id || "anonymous";

        return userId;
    };
    const currentUserId = getCurrentUserId();
    const currentUserMessages = allUserHistories[currentUserId] || [];

    const [messages, setMessages] = useState<ChatMessageType[]>(currentUserMessages);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);

    // Update the specific user's history when messages change
    useEffect(() => {
        setAllUserHistories((prev) => ({
            ...prev,
            [currentUserId]: messages,
        }));
    }, [messages, currentUserId]);

    // Load messages when user changes
    useEffect(() => {
        setMessages(currentUserMessages);
    }, [currentUserId]);

    // Keep track of current streaming message
    const streamingMessageRef = useRef<ChatMessageType | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInput(e.target.value);
    }, []);

    const handleSubmit = useCallback(
        async (e?: React.FormEvent<HTMLFormElement>) => {
            if (e) {
                e.preventDefault();
            }

            if (!input.trim() || isLoading || isStreaming) {
                console.log("⚠️ SUBMIT BLOCKED:", { input: input.trim(), isLoading, isStreaming });
                return;
            }

            // Clean up any existing stream before starting new one
            if (abortControllerRef.current) {
                console.log("🧹 CLEANING UP EXISTING STREAM");
                chatbotService.cancelStream(abortControllerRef.current);
                abortControllerRef.current = null;
            }
            if (streamingMessageRef.current) {
                console.log("🧹 CLEANING UP EXISTING MESSAGE REF");
                streamingMessageRef.current = null;
            }

            const userMessage: ChatMessageType = {
                id: DateTime.now().toMillis().toString(),
                role: ChatMessageRole.USER,
                content: input.trim(),
                createdAt: DateTime.now().toISO(),
            };

            const newMessages = [...messages, userMessage];

            setMessages(newMessages);
            setInput("");
            setIsLoading(true);
            setIsStreaming(true);

            // Initialize streaming message
            const streamingMessageId = (DateTime.now().toMillis() + 1).toString();
            streamingMessageRef.current = {
                id: streamingMessageId,
                role: ChatMessageRole.ASSISTANT,
                content: "",
                createdAt: DateTime.now().toISO(),
            };

            try {
                // Handle stream chunks based on backend StreamEventData format
                const onChunk = createStreamEventHandler(streamingMessageRef, streamingMessageId, setMessages);

                const onComplete = () => {
                    // Check if we already processed this completion
                    if (!streamingMessageRef.current) {
                        console.log("⚠️ ONCOMPLETE ALREADY PROCESSED - IGNORING");
                        return;
                    }

                    // Capture the complete message before setting to null
                    const completeMessage = { ...streamingMessageRef.current };
                    console.log("💾 CAPTURING COMPLETE MESSAGE:", completeMessage);

                    // Ensure the complete message is saved to state
                    setMessages((currentMessages) => {
                        console.log("💾 ONCOMPLETE - BEFORE SAVING:", {
                            currentMessagesCount: currentMessages.length,
                            currentMessages: currentMessages.map((m) => ({
                                id: m.id,
                                role: m.role,
                                content: (m.content || "").substring(0, 20) + "...",
                            })),
                            streamingMessageId,
                            completeMessage: completeMessage,
                        });

                        const updatedMessages = [...currentMessages];
                        const lastMessageIndex = updatedMessages.length - 1;
                        if (lastMessageIndex >= 0 && updatedMessages[lastMessageIndex].id === streamingMessageId) {
                            updatedMessages[lastMessageIndex] = completeMessage;
                            console.log("💾 COMPLETE MESSAGE SAVED TO STATE:", {
                                messageId: streamingMessageId,
                                content: completeMessage.content,
                                totalMessages: updatedMessages.length,
                            });
                        } else {
                            console.warn("⚠️ ONCOMPLETE - MESSAGE ID MISMATCH:", {
                                expectedId: streamingMessageId,
                                actualId: updatedMessages[lastMessageIndex]?.id,
                                lastMessageIndex,
                                totalMessages: updatedMessages.length,
                            });
                        }

                        console.log("💾 ONCOMPLETE - AFTER SAVING:", {
                            updatedMessagesCount: updatedMessages.length,
                            updatedMessages: updatedMessages.map((m) => ({
                                id: m.id,
                                role: m.role,
                                content: (m.content || "").substring(0, 20) + "...",
                            })),
                        });

                        return updatedMessages;
                    });

                    setIsLoading(false);
                    setIsStreaming(false);

                    streamingMessageRef.current = null;
                    abortControllerRef.current = null;
                };

                const onError = (error: Error) => {
                    console.error("Error streaming AI response:", error);
                    setIsLoading(false);
                    setIsStreaming(false);

                    const errorMessage: ChatMessageType = {
                        id: streamingMessageId,
                        role: ChatMessageRole.ASSISTANT,
                        content: "Sorry, there was an error processing your message. Please try again.",
                        createdAt: DateTime.now().toISO(),
                    };

                    setMessages((currentMessages) => {
                        const updatedMessages = [...currentMessages];
                        const lastMessageIndex = updatedMessages.length - 1;
                        if (lastMessageIndex >= 0) {
                            updatedMessages[lastMessageIndex] = errorMessage;
                        }
                        return updatedMessages;
                    });

                    streamingMessageRef.current = null;
                    abortControllerRef.current = null;
                };

                // Start streaming
                const abortController = await chatbotService.sendMessageStream(
                    {
                        message: input.trim(),
                        userId: currentUserId,
                        chatId,
                        auth,
                    },
                    {
                        onChunk,
                        onComplete,
                        onError,
                    }
                );

                abortControllerRef.current = abortController;
            } catch (error) {
                console.error("Error starting stream:", error);
                setIsLoading(false);
                setIsStreaming(false);

                const errorMessage: ChatMessageType = {
                    id: streamingMessageId,
                    role: ChatMessageRole.ASSISTANT,
                    content: "Sorry, there was an error processing your message. Please try again.",
                    createdAt: DateTime.now().toISO(),
                };

                setMessages((currentMessages) => {
                    const updatedMessages = [...currentMessages];
                    const lastMessageIndex = updatedMessages.length - 1;
                    if (lastMessageIndex >= 0) {
                        updatedMessages[lastMessageIndex] = errorMessage;
                    }
                    return updatedMessages;
                });

                streamingMessageRef.current = null;
                abortControllerRef.current = null;
            }
        },
        [input, messages, isLoading, isStreaming]
    );

    const stopStreaming = useCallback(() => {
        if (abortControllerRef.current && isStreaming) {
            chatbotService.cancelStream(abortControllerRef.current);
            setIsLoading(false);
            setIsStreaming(false);
            abortControllerRef.current = null;
            streamingMessageRef.current = null;
        }
    }, [isStreaming]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                chatbotService.cancelStream(abortControllerRef.current);
            }
        };
    }, []);

    const clearHistory = useCallback(async () => {
        console.log("🗑️ CLEARING HISTORY for user:", currentUserId);
        try {
            // Stop any ongoing stream first
            stopStreaming();

            // Clear backend history if we have a chatId (sessionId)
            if (chatId) {
                await chatbotService.clearChatHistory(chatId, currentUserId);
                console.log("✅ Backend chat history cleared");
            }

            // Clear frontend state
            setMessages([]);
            setAllUserHistories((prev) => {
                const updated = { ...prev };
                delete updated[currentUserId];
                return updated;
            });

            console.log("✅ Frontend chat history cleared");
        } catch (error) {
            console.error("❌ Failed to clear chat history:", error);
            // Still clear frontend even if backend fails
            setMessages([]);
            setAllUserHistories((prev) => {
                const updated = { ...prev };
                delete updated[currentUserId];
                return updated;
            });
        }
    }, [stopStreaming, currentUserId, chatId]);

    return {
        messages,
        input,
        handleInputChange,
        handleSubmit,
        clearHistory,
        isLoading,
        isStreaming, // Additional state for streaming-specific UI
        stopStreaming, // Additional method to stop streaming
    } satisfies UseChatStreamReturn;
}
